import React, { useState, useRef } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { useAppSelector } from '../../../hooks/redux';
import { Order } from '@m4a3/api-client';
import { getShortIdForOrder, isOrderLate, formatOrderStatus } from '../../../utils/orderUtils';
import useInterval from '../../../utils/useInterval';
import { selectOverweightColorPairingsForOrder } from '../../../selectors/ordersSelector';
import { getDescriptionForCollectorStatus } from '../../Restaurant/types/CollectorStatus';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';

interface WeigherAppOrderCardProps {
  order: Order;
  orderIdWeightMap: {[key: string]: number};
  isWithCollector: (order: any) => boolean;
  shouldShowWeatherWarning?: boolean;
  onClick?: (order: Order) => void; 
}

export const WeigherAppOrderCard = (props: WeigherAppOrderCardProps) => {
  // =========================================
  // State & Redux Hooks
  // =========================================
  const overDueRef = useRef(false);

  // Get linked orders color information
  const linkedOrdersForOverweightOrders = useAppSelector(state =>
    selectOverweightColorPairingsForOrder(state, props.order.orderShortId)
  );

  const [update, setUpdate] = useState(0);
  const [overDue, setOverDue] = useState(isOrderLate(props.order));
  
  // Use merchant data directly from order (provided by SSE)
  const merchantName = props.order.outletName || "Unknown Merchant";
  const merchantLogoUrl = (props.order as any).merchantImageUrl;

  // =========================================
  // Time Calculation Constants
  // =========================================
  const expectedDelivery = new Date(props.order.expectedDeliveryDate).getTime();
  const currentTime = new Date().getTime();
  const THREE_MINUTES_FOR_DELIVERY = 180000;
  const TIME_FOR_TAKEOFF = expectedDelivery - THREE_MINUTES_FOR_DELIVERY;
  const timeDifference = TIME_FOR_TAKEOFF - currentTime;
  
  // =========================================
  // Lifecycle & Interval Hooks
  // =========================================
  // Check overdue status every second
  useInterval(() => {
    if (isOrderLate(props.order) && overDueRef.current === false) {
      overDueRef.current = isOrderLate(props.order);
      setOverDue(true);
    }
  }, 1000);

  // Update time display every second
  useInterval(() => {
    setUpdate(update + 1);
  }, 1000);

  // =========================================
  // Time Formatting Utility Functions
  // =========================================
  function getHours(diff: number) {
    return Math.floor(diff / 3600000);
  }

  function getMinutes(diff: number) {
    return Math.floor(diff / 60000);
  }

  function getSeconds(diff: number) {
    return Math.floor((diff / 1000) % 60);
  }

  function getDueTime(diff: number) {
    if (getMinutes(diff) > 60 * 48) {
      return 'in the future';
    } else if (getMinutes(diff) > 60 * 24 && getMinutes(diff) < 60 * 48) {
      return 'tomorrow';
    } else if (getMinutes(diff) > 60) {
      return `in ${Math.floor(diff / 60000 / 60)} hrs and ${Math.floor(
        (diff / 60000) % 60
      )} mins`;
    } else {
      return `in ${getMinutes(diff)} mins`;
    }
  }

  const numberToPaddedString = (num: number): string => {
    return `${num}`.padStart(2, '0');
  };

  // =========================================
  // Display Text and Formatting Functions
  // =========================================
  // Calculate time display text
  const getTimeDisplayText = (): string => {
    const tenMins = 10;
    const isDueSoon = getMinutes(timeDifference) < tenMins;
    
    if (!overDue && !isDueSoon) {
      return `${getDueTime(timeDifference)}`;
    }

    if (!overDue && isDueSoon) {
      return `${numberToPaddedString(
        getMinutes(timeDifference)
      )}:${numberToPaddedString(getSeconds(timeDifference))} min`;
    }

    if (overDue) {
      if (getMinutes(-timeDifference) >= 60) {
        return `${getHours(-timeDifference)}:${numberToPaddedString(
          getMinutes(-timeDifference) % 60
        )}:${numberToPaddedString(getSeconds(-timeDifference))}`;
      }
      return `${numberToPaddedString(
        getMinutes(-timeDifference)
      )}:${numberToPaddedString(getSeconds(-timeDifference))}`;
    }
    
    return "Unknown"; // Fallback
  };

  // Get text and background colors based on status
  function getTextColor() {
    if (overDue) {
      return '#ED0505';
    }
    return '#008A00';
  }

  // =========================================
  // Order Data Formatting Functions
  // =========================================
  // Format linked orders text
  const getLinkedOrdersText = () => {
    if (!linkedOrdersForOverweightOrders?.linkedOverweightOrderIds?.length) {
      return null;
    }
    
    const shortOrderIds = linkedOrdersForOverweightOrders.linkedOverweightOrderIds;
    return shortOrderIds.join(', ');
  };

  const getOrderWeight = (): string | null => {
    if (props.orderIdWeightMap[props.order.id]) {
      return `${props.orderIdWeightMap[props.order.id]}kg`
    } else if (props.order.actualWeight) {
      return `${props.order.actualWeight}kg`
    } else {
      return null;
    }
  }
  
  // Check if order is with collector
  const showCollectorId = props.isWithCollector && props.isWithCollector(props.order);
  
  // =========================================
  // Component Render
  // =========================================
  return (
    <Paper 
      elevation={2} 
      sx={{ 
        position: 'relative',
        width: '320px',
        maxWidth: '100%',
        mb: 2,
        borderRadius: '16px',
        overflow: 'hidden',
        backgroundColor: 'white',
        cursor: 'pointer', 
        '&:hover': {
          boxShadow: 3, 
        }
      }}
      onClick={() => props.onClick && props.onClick(props.order)} 
    >
      <Box 
        display="flex" 
        flexDirection="column"
        sx={{ 
          backgroundColor: linkedOrdersForOverweightOrders?.color ?? 'white',
        }}
      >
        {/* Main card content */}
        <Box 
          display="flex" 
          flexDirection="column"
          sx={{ 
            p: 2,
          }}
        >
          {/* Weather warning */}
          {props.shouldShowWeatherWarning && (
            <Box 
              sx={{ 
                display: 'flex',
                padding: '4px 10px',
                backgroundColor: '#FFFBEA', 
                border: '1px solid #FEF3C7',
                borderRadius: '8px',
                alignItems: 'center',
                marginBottom: '8px',
              }}
            >
              <ExclamationTriangleIcon
                className="h-5 w-5 text-yellow-400 mr-1"
                aria-hidden="true"
              />
              <Typography 
                variant="caption" 
                sx={{ 
                  fontWeight: 'medium',
                  color: '#92400E'
                }}
              >
                High windspeed
              </Typography>
            </Box>
          )}

          {/* First line: Merchant logo and merchant name */}
          <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
            <Box className="flex" sx={{ width: 48, height: 48, border: '1px solid #D9DBE9', borderRadius: 2, overflow: 'hidden', flexShrink: 0 }}>
              {merchantLogoUrl && (
                <Box className="block relative overflow-hidden" sx={{ width: 48, height: 48 }}>
                  <img
                    alt="store logo"
                    src={merchantLogoUrl}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%'
                    }}
                  />
                </Box>
              )}
            </Box>

            <Box 
              sx={{ 
                marginLeft: '16px',
                flexGrow: 1,
                minWidth: 0
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 'bold', 
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {merchantName}
              </Typography>
            </Box>
          </Box>

          {/* Second line: Order ID and weight */}
          <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
            <Box 
              sx={{ 
                flexGrow: 0,
                flexShrink: 0,
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '8px',
                border: '1px solid #E7E7E7',
                mr: 2
              }}
            >
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 'bold', 
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {(getShortIdForOrder(props.order) || "Unknown").slice(-5)}
              </Typography>
            </Box>
          
            <Box sx={{ flexGrow: 0, flexShrink: 0 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: getOrderWeight() ? 'black' : 'red',
                  fontWeight: 'bold', 
                  fontSize: '18px', 
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  border: '1px solid #E7E7E7',
                  borderRadius: '8px',
                  p: 1,
                  whiteSpace: 'nowrap',
                  backgroundColor: 'white'
                }}
              >
                {getOrderWeight() ?? '0kg'}
              </Typography>
            </Box>
          </Box>
          
          {/* Third line: Time and linked orders */}
          <Box display="flex" alignItems="center">
            <Box 
              sx={{ 
                height: '32px',
                padding: '4px 12px',
                display: 'flex', 
                alignItems: 'center',
                border: '1px solid #E7E7E7',
                borderRadius: '8px',
                flexShrink: 0,
                backgroundColor: 'white',
                mr: 2
              }}
            >
              <Box 
                component="span" 
                sx={{ 
                  width: '8px', 
                  height: '8px', 
                  borderRadius: '50%', 
                  bgcolor: overDue ? '#ED0505' : '#4CAF50', 
                  mr: 1 
                }} 
              />
              <Typography 
                variant="body2"
                sx={{
                  color: getTextColor()
                }}
              >
                {getTimeDisplayText()}
              </Typography>
            </Box>
            
            {getLinkedOrdersText() ? (
              <Box 
                sx={{ 
                  display: 'flex',
                  padding: '8px',
                  alignItems: 'center',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  border: '1px solid #E7E7E7',
                  width: 'fit-content'
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ 
                    color: 'black',
                    fontWeight: 600,
                    maxWidth: '100px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Linked: {getLinkedOrdersText()}
                </Typography>
              </Box>
            ): null}
          </Box>
        </Box>

        <Box 
          sx={{ 
            backgroundColor: 'white',
            padding: '8px 16px',
            borderTop: '1px solid rgba(0, 0, 0, 0.1)',
          }}
        >
          <Typography 
            variant="body2"
            sx={{
              fontWeight: 'medium',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Box component="span" sx={{ fontWeight: 'bold', mr: 1 }}>Order status:</Box>
            {formatOrderStatus(props.order.status)}
          </Typography>
        </Box>
        
        {/* Collector ID row - only shown when isWithCollector returns true */}
        {showCollectorId ? (
          <Box 
            sx={{ 
              backgroundColor: 'white',
              padding: '8px 16px',
              borderTop: '1px solid rgba(0, 0, 0, 0.1)',
            }}
          >
            <Typography 
              variant="body2"
              sx={{
                fontWeight: 'medium',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              {getDescriptionForCollectorStatus(props.order.collectorStatus, props.order.collectorId)}
            </Typography>
          </Box>
        ): null}
      </Box>
    </Paper>
  );
};